import type { QueryClient } from '@tanstack/react-query';
import { webhookKeys, type Webhook } from './entity';

/**
 * Reporting-webhook cache helpers. The list slot holds a bare `Webhook[]`
 * (see `queries.ts`), so list patches are plain array ops.
 *
 * Writes patch the cached list **in place** rather than invalidating it —
 * signing-key rotation in particular must not drop and refetch the row, or
 * the freshly-returned key would vanish mid-view before the user can copy it
 * (the Ember model did this via `store.pushPayload`). Create/update/delete
 * patch in place too; the list route reads at `staleTime: 0`, so the next
 * visit refetches anyway.
 */
function patchWebhookLists(qc: QueryClient, workspaceId: number, patch: (list: Webhook[]) => Webhook[]): void {
  const listQueries = qc.getQueryCache().findAll({ queryKey: webhookKeys.lists(workspaceId) });
  for (const query of listQueries) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const existing = query.state.data as Webhook[] | undefined;
    if (existing == null) continue;
    qc.setQueryData(query.queryKey, patch(existing));
  }
}

/**
 * Write a saved webhook into its detail slot and upsert it into every cached
 * list (replace by id when present, otherwise prepend — the same helper works
 * for create and update).
 */
export function setWebhookCache(qc: QueryClient, workspaceId: number, updated: Webhook): void {
  qc.setQueryData(webhookKeys.detail(workspaceId, updated.id), updated);
  patchWebhookLists(qc, workspaceId, (list) => {
    const index = list.findIndex((w) => w.id === updated.id);
    return index === -1 ? [updated, ...list] : list.map((w) => (w.id === updated.id ? updated : w));
  });
}

/** Drop a deleted webhook's detail slot and remove it from every cached list. */
export function removeWebhookCache(qc: QueryClient, workspaceId: number, webhookId: string): void {
  // Only inactive: the edit page that triggered the delete still observes the
  // detail key (suspense) while it navigates away; removing an observed query
  // forces a doomed refetch of the just-deleted row. It's GC'd when the page
  // unmounts.
  qc.removeQueries({ queryKey: webhookKeys.detail(workspaceId, webhookId), type: 'inactive' });
  patchWebhookLists(qc, workspaceId, (list) => list.filter((w) => w.id !== webhookId));
}
